import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight, Share2 } from "lucide-react";
import Panda from "./Panda";

// Generic full-screen "story" player. slides: [{ bg, icon, title, big, sub, panda, dark, last, cta }]
const StoryPlayer = ({ slides, onClose, onFinish, onSave, saving = false, testId = "story" }) => {
  const [i, setI] = useState(0);
  const next = () => setI((n) => Math.min(n + 1, slides.length - 1));

  useEffect(() => {
    if (!slides.length || slides[i].last) return;
    const t = setTimeout(next, 4500);
    return () => clearTimeout(t);
  }, [i, slides.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); } if (e.key === "ArrowLeft") setI((n) => Math.max(n - 1, 0)); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, slides.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!slides.length) return null;
  const s = slides[i];
  const Icon = s.icon;
  const fg = s.dark ? "#5a4600" : "#ffffff";
  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col select-none transition-colors duration-700" style={{ background: s.bg, color: fg, margin: 0 }} onClick={() => !s.last && next()} data-testid={testId} data-index={i}>
      <div className="flex gap-1 p-4 pr-16">{slides.map((_, k) => <span key={k} className={`h-1.5 flex-1 rounded-full transition-colors ${k <= i ? "bg-current" : "bg-white/30"}`} />)}</div>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-3 right-4 w-11 h-11 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center" aria-label="Close" data-testid={`${testId}-close`}><X /></button>
      <div key={i} className="flex-1 flex flex-col items-center justify-center text-center px-6 yir-slide">
        <div className="flex items-end justify-center gap-2">
          {(s.pandas || [s.panda]).filter(Boolean).map((p, k) => <div key={k} className="yir-pop" style={{ animationDelay: `${k * 120}ms` }}><Panda size={(s.pandas?.length || 1) > 1 ? 90 : 120} accessory={p.accessory} scarf={p.scarf} item={s.item} animate /></div>)}
        </div>
        <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest opacity-90 yir-rise" style={{ animationDelay: "150ms" }}><Icon size={18} /> {s.title}</p>
        <p className="mt-2 font-bold leading-none yir-rise" style={{ fontFamily: "Literata, serif", fontSize: String(s.big).length > 6 ? "clamp(2.2rem, 8vw, 5rem)" : "clamp(4rem, 16vw, 9rem)", animationDelay: "300ms" }} data-testid={`${testId}-big`}>{s.big}</p>
        <p className="mt-4 max-w-md text-lg sm:text-xl font-medium opacity-95 yir-rise" style={{ animationDelay: "450ms" }} data-testid={`${testId}-sub`}>{s.sub}</p>
        {s.last && s.cta && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 yir-rise" style={{ animationDelay: "600ms" }}>
            <button onClick={(e) => { e.stopPropagation(); onFinish(); }} className="btn-pill-lg bg-white text-[#0c4a6e] hover:bg-[#e9f9fd] shadow-lg inline-flex items-center gap-2" data-testid={`${testId}-report-card`}>{s.cta} <ChevronRight /></button>
            {onSave && <button onClick={(e) => { e.stopPropagation(); onSave(); }} disabled={saving} className="btn-pill-lg bg-white/20 hover:bg-white/35 text-white border-2 border-white/60 inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${testId}-save`}><Share2 size={18} /> {saving ? "Preparing..." : "Save & share as image"}</button>}
          </div>
        )}
      </div>
      {!s.last && <p className="pb-6 text-center text-xs opacity-75">Tap to continue</p>}
    </div>,
    document.body
  );
};

export default StoryPlayer;
